import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertTriangle, Layers } from 'lucide-react';
import EmptyState from './EmptyState';
import { BUTTON } from './utils/uiTokens';
import useDatosVistaGeneral, { VISTA_GENERAL_SLUG } from './utils/useDatosVistaGeneral';

/**
 * Vista general: el mismo shell de un proyecto (tablero / lista / calendario /
 * gantt) pero alimentado con las tareas de TODOS los proyectos del usuario.
 *
 * Vive bajo /proyectos/vista-general, dentro de la ruta /proyectos/:slug — asi
 * las vistas hijas reusan tal cual su navegacion (`/proyectos/${slug}/lista/...`)
 * y su slide-over sin ramas especiales. El slug tiene 13 caracteres y las
 * columnas de slug estan topeadas en 12, de modo que ningun proyecto real puede
 * chocar con el.
 *
 * Diferencia clave con un proyecto: aca no hay estados propios. Cada proyecto
 * define sus sub-estados, asi que el board y los agrupadores trabajan contra las
 * 4 categorias fijas (estados "virtuales") y, al mover una tarea, se resuelve el
 * estado real dentro del proyecto de esa tarea.
 */

export { VISTA_GENERAL_SLUG };

const VistaGeneralView = () => {
  const navigate = useNavigate();
  // Tareas de todos los proyectos + proyecto sintetico (ver useDatosVistaGeneral).
  const { loading, error, proyectos, tareas, contexto } = useDatosVistaGeneral();

  const totalTareas = tareas.length;

  // ----- Render -----

  const tabBase = 'relative px-3 py-1.5 text-sm font-medium tracking-tight transition-colors duration-150';
  const tabIdle = 'text-slate-500 hover:text-slate-900';
  const tabActive = 'text-slate-900';

  if (loading) {
    return (
      <div className="bg-[#FAFAFA] flex flex-col -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
        <header className="flex-shrink-0 bg-white border-b border-slate-200/80">
          <div className="flex items-center justify-between h-[56px] px-6 skeleton">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-3 w-16 bg-slate-100 rounded" />
              <div className="h-5 w-px bg-slate-200" />
              <div className="h-4 w-32 bg-slate-100 rounded" />
            </div>
            <div className="hidden md:flex items-center gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-4 w-16 bg-slate-100 rounded" />
              ))}
            </div>
            <div className="h-3 w-16 bg-slate-100 rounded" />
          </div>
        </header>
        <main className="flex-1 px-6 py-4 skeleton">
          <div className="h-4 w-32 bg-slate-100 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, col) => (
              <div key={col} className="bg-white border border-slate-200/70 rounded-lg p-3">
                <div className="h-3 w-20 bg-slate-100 rounded mb-3" />
                {[...Array(3)].map((_, row) => (
                  <div key={row} className="h-12 bg-slate-50 rounded mb-2" />
                ))}
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#FAFAFA] flex items-center justify-center px-6 -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)]">
        <EmptyState
          icon={AlertTriangle}
          title={error}
          variant="centered"
          action={
            <button
              onClick={() => navigate('/proyectos')}
              className={`${BUTTON.secondary} ${BUTTON.size.md}`}
            >
              <ArrowLeft size={14} />
              Volver a proyectos
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAFA] flex flex-col -m-4 md:-m-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] h-[calc(100%+2rem)] md:h-[calc(100%+4rem)] overflow-hidden">
      {/* Navbar nivel 2 */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200/80">
        <div className="flex items-center justify-between h-[56px] px-6">
          {/* Izquierda: volver + titulo */}
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/proyectos"
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors group"
            >
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
              <span className="hidden sm:inline">Proyectos</span>
            </Link>

            <div className="h-5 w-px bg-slate-200" />

            <div className="flex items-center gap-2 min-w-0">
              <Layers size={14} className="text-slate-400 flex-shrink-0" strokeWidth={2} />
              <span className="text-sm font-semibold tracking-tight text-slate-900 whitespace-nowrap">
                Vista general
              </span>
              <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
                {proyectos.length} {proyectos.length === 1 ? 'proyecto' : 'proyectos'}
              </span>
            </div>
          </div>

          {/* Centro: tabs de vista */}
          <nav className="hidden md:flex items-center gap-1 -mb-px">
            {[
              { to: `/proyectos/${VISTA_GENERAL_SLUG}`, label: 'Tablero', end: true },
              { to: `/proyectos/${VISTA_GENERAL_SLUG}/lista`, label: 'Lista' },
              { to: `/proyectos/${VISTA_GENERAL_SLUG}/calendario`, label: 'Calendario' },
              { to: `/proyectos/${VISTA_GENERAL_SLUG}/gantt`, label: 'Gantt' },
            ].map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) => `${tabBase} ${isActive ? tabActive : tabIdle}`}
              >
                {({ isActive }) => (
                  <>
                    {tab.label}
                    {isActive && (
                      <motion.div
                        layoutId="vista-general-tab-underline"
                        className="absolute left-3 right-3 -bottom-px h-[2px] rounded-full bg-cyan-500"
                        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Derecha: total agregado. No hay "Nueva tarea" porque una tarea
              siempre nace dentro de un proyecto. */}
          <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
            {totalTareas} {totalTareas === 1 ? 'tarea' : 'tareas'}
          </span>
        </div>
      </header>

      {/* Contenido */}
      <main className="flex-1 overflow-hidden">
        <Outlet context={contexto} />
      </main>
    </div>
  );
};

export default VistaGeneralView;
